// Fælles byggeklodser til skærmene: API-adgang, intern navigation, data-hooks, ikoner og dialoger.
import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useBrandVisning } from './brandValg';
import type { Api } from './api/api';
import { ApiFejl } from './api/api';
import type { BookingStatus, Sprog } from '../core/typer';
import { klokken } from '../core/dato';
import { SPROG, erRtl, oversaet, ORD } from './sprog';
import { saetSidesprog, tilfoejOrd } from './oversaetter';
import { live, type LiveStatus } from './api/live';
import pixieIkon from './billeder/pixie-ikon.webp';

// ---------- API ----------
export const ApiKontekst = createContext<Api | null>(null);
export function useApi(): Api { const a = useContext(ApiKontekst); if (!a) throw new Error('Mangler API'); return a; }

// ---------- Navigation ----------
// Artifact-visningen lader ikke "#/sti" nå siden, så navigationen holdes i appen. Links med href="#/..." fanges.
let rute = '/';
const ruteLyttere = new Set<() => void>();
const historik: string[] = [];
/** rul: false bevarer scroll-positionen – til lister, hvor et klik kun åbner en detalje ved siden af (fx CRM-ringelisten). */
export function gaaTil(sti: string, erstat = false, rul = true) {
  if (sti === rute) return;
  if (!erstat) historik.push(rute);
  rute = sti;
  try { history.replaceState(null, '', '#' + sti); } catch { /* visningen tillader det ikke altid */ }
  if (rul) try { window.scrollTo(0, 0); } catch { /* ok */ }
  ruteLyttere.forEach((f) => f());
}
export function tilbage(fallback: string) { const f = historik.pop(); gaaTil(f ?? fallback, true); }
export function saetStartRute(sti: string) { rute = sti; }
export function useRute(): string {
  return useSyncExternalStore((f) => { ruteLyttere.add(f); return () => ruteLyttere.delete(f); }, () => rute);
}
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey) return;
    const a = (e.target as Element | null)?.closest?.('a[href^="#/"]');
    if (!a) return;
    e.preventDefault();
    gaaTil(a.getAttribute('href')!.slice(1));
  });
}

// ---------- Tema ----------
export type Tema = 'auto' | 'lys' | 'moerk';
export function hentTema(): Tema { try { return (localStorage.getItem('pixie-tema') as Tema) || 'auto'; } catch { return 'auto'; } }
export function anvendTema(t: Tema) {
  const r = document.documentElement;
  if (t === 'auto') r.removeAttribute('data-theme'); else r.setAttribute('data-theme', t === 'moerk' ? 'dark' : 'light');
}
export function saetTema(t: Tema) { try { localStorage.setItem('pixie-tema', t); } catch { /* ok */ } anvendTema(t); temaLyttere.forEach((f) => f()); }
const temaLyttere = new Set<() => void>();
export function useTema(): Tema { return useSyncExternalStore((f) => { temaLyttere.add(f); return () => temaLyttere.delete(f); }, hentTema); }
export function erMoerk(): boolean {
  const t = hentTema();
  if (t !== 'auto') return t === 'moerk';
  try { return matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
}

// ---------- Data ----------
/** Henter data og henter igen, når data ændres (også fra en anden fane). */
export function useData<T>(hent: () => Promise<T>, afh: unknown[] = []): { data: T | undefined; fejl: string | null; henter: boolean; genhent: () => void } {
  const api = useApi();
  const [data, setData] = useState<T>();
  const [fejl, setFejl] = useState<string | null>(null);
  const [henter, setHenter] = useState(true);
  const [n, setN] = useState(0);
  const ref = useRef(hent); ref.current = hent;
  useEffect(() => api.lyt(() => setN((x) => x + 1)), [api]);
  useEffect(() => {
    let aktiv = true; setHenter(true);
    ref.current().then((d) => { if (aktiv) { setData(d); setFejl(null); } }).catch((e) => { if (aktiv) setFejl(fejlTekst(e)); }).finally(() => { if (aktiv) setHenter(false); });
    return () => { aktiv = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...afh, n]);
  return { data, fejl, henter, genhent: () => setN((x) => x + 1) };
}

export function fejlTekst(e: unknown): string {
  if (e instanceof ApiFejl) return e.brugerBesked;
  if (e instanceof Error && e.message) return e.message;
  return 'Noget gik galt. Prøv igen.';
}

/** Kører en handling med "arbejder"-tilstand, fejlvisning og evt. besked bagefter. */
export function useHandling() {
  const [arbejder, setArbejder] = useState(false);
  const [fejl, setFejl] = useState<string | null>(null);
  const [felter, setFelter] = useState<Record<string, string>>({});
  const koer = useCallback(async <T,>(f: () => Promise<T>, ok?: string): Promise<T | undefined> => {
    setArbejder(true); setFejl(null); setFelter({});
    try { const r = await f(); if (ok) visToast(ok); return r; }
    catch (e) { setFejl(fejlTekst(e)); if (e instanceof ApiFejl) setFelter(e.felter); return undefined; }
    finally { setArbejder(false); }
  }, []);
  return { koer, arbejder, fejl, felter, setFejl };
}

// ---------- Toast ----------
let toastTekst: string | null = null; let toastTimer: ReturnType<typeof setTimeout> | undefined;
const toastLyttere = new Set<() => void>();
export function visToast(t: string) { toastTekst = t; toastLyttere.forEach((f) => f()); clearTimeout(toastTimer); toastTimer = setTimeout(() => { toastTekst = null; toastLyttere.forEach((f) => f()); }, 3200); }
export function Toast() {
  const t = useSyncExternalStore((f) => { toastLyttere.add(f); return () => toastLyttere.delete(f); }, () => toastTekst);
  return t ? <div className="toast" role="status">{t}</div> : null;
}

// ---------- Ikoner ----------
const STIER: Record<string, string> = {
  hjem: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z',
  del: 'M12 3v12M8 7l4-4 4 4M6 11H5v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9h-1',
  kalender: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM4 10h16M8 2v4M16 2v4',
  liste: 'M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01',
  ordre: 'M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0',
  betaling: 'M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM2 10h20M6 15h4',
  kunder: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  skjold: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4',
  bil: 'M5 17h14M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0M3 17v-5l2-5h14l2 5v5M3 12h18',
  tag: 'M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8zM7 7h.01',
  steamer: 'M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10M20 21a8 8 0 0 0-16 0',
  globus: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3',
  zone: 'M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5',
  procent: 'M19 5 5 19M6.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M17.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5',
  stjerne: 'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z',
  tandhjul: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  noegle: 'M21 2l-2 2m-7.6 7.6a5.5 5.5 0 1 1-7.8 7.8 5.5 5.5 0 0 1 7.8-7.8zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4',
  lyn: 'M13 2 3 14h9l-1 8 10-12h-9z',
  formular: 'M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1zM16 5h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2M8 11h8M8 15h5',
  puslespil: 'M10 3h4v2a2 2 0 1 0 4 0V3h3v7h-2a2 2 0 1 0 0 4h2v7h-7v-2a2 2 0 1 0-4 0v2H3v-7h2a2 2 0 1 0 0-4H3V3z',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6',
  plus: 'M12 5v14M5 12h14',
  soeg: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35',
  indbakke: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  x: 'M18 6 6 18M6 6l12 12',
  mere: 'M4 6h16M4 12h16M4 18h16',
  prikker: 'M4.5 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M10.5 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0M16.5 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0',
  greb: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  sol: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  maane: 'M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z',
  logud: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  hoejre: 'M9 18l6-6-6-6', venstre: 'M15 18l-6-6 6-6', ned: 'M6 9l6 6 6-6',
  check: 'M20 6 9 17l-5-5', ur: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2',
  telefon: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z',
  kamera: 'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  advarsel: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  oeje: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  oejeluk: 'M17.9 17.9A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.5 18.5 0 0 1 5.1-5.9M9.9 4.2A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.2 3.2M14.1 14.1a3 3 0 1 1-4.2-4.2M1 1l22 22',
  kopier: 'M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zM5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
  slet: 'M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2',
  rediger: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4z',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01',
  smartplan: 'M3 4h18v16H3zM3 9h18M8 9v11M8 13h6M14 16h5',
  laas: 'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2zM7 11V7a5 5 0 0 1 10 0v4',
  laasop: 'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2zM7 11V7a5 5 0 0 1 9.9-1',
  dokument: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h8',
  afventer: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12h.01M12 12h.01M16 12h.01',
  version: 'M12 2 2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  bruger: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  op: 'M18 15l-6-6-6 6', gentag: 'M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  besked: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  rute: 'M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3zM9 3v15M15 6v15', pin: 'M12 22s7-7.5 7-13a7 7 0 0 0-14 0c0 5.5 7 13 7 13zM12 11.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  clip: 'M21.4 11l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5',
  mik: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3', pen: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  smil: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01', svar: 'M9 17l-5-5 5-5M20 18v-2a4 4 0 0 0-4-4H4',
  gruppe: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  stop: 'M6 6h12v12H6z', afspil: 'M6 4l14 8-14 8z', billede: 'M3 5h18v14H3zM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM21 15l-5-5L5 19',
  dobbeltcheck: 'M2 12l5 5L18 6M12 16l1 1L22 8',
  klokke: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0', graf: 'M23 6l-9.5 9.5-5-5L1 18M17 6h6v6',
};
export function Ikon({ navn, lille, titel }: { navn: keyof typeof STIER | string; lille?: boolean; titel?: string }) {
  return (
    <svg className={'ikon' + (lille ? ' lille' : '')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden={titel ? undefined : true} role={titel ? 'img' : undefined}>
      {titel && <title>{titel}</title>}
      <path d={STIER[navn] ?? STIER.info} />
    </svg>
  );
}

// ---------- Små komponenter ----------
export function Felt({ etiket, fejl, hjaelp, children, className }: { etiket: string; fejl?: string; hjaelp?: string; children: ReactNode; className?: string }) {
  return (
    <label className={'felt' + (fejl ? ' har-fejl' : '') + (className ? ' ' + className : '')}>
      <span>{etiket}</span>
      {children}
      {hjaelp && !fejl && <small className="hjaelp">{hjaelp}</small>}
      {fejl && <small className="fejltekst" role="alert">{fejl}</small>}
    </label>
  );
}
export function KodeFelt({ vaerdi, saet, autoComplete = 'current-password', id }: { vaerdi: string; saet: (v: string) => void; autoComplete?: string; id?: string }) {
  const [vis, setVis] = useState(false);
  return (
    <div className="kodefelt">
      <input id={id} type={vis ? 'text' : 'password'} value={vaerdi} onChange={(e) => saet(e.target.value)} autoComplete={autoComplete} />
      <button type="button" className="ikonknap" onClick={() => setVis((v) => !v)} aria-label={vis ? 'Skjul adgangskode' : 'Vis adgangskode'}><Ikon navn={vis ? 'oejeluk' : 'oeje'} lille /></button>
    </div>
  );
}
export function FejlBesked({ fejl }: { fejl: string | null | undefined }) { return fejl ? <div className="besked fejl" role="alert">{fejl}</div> : null; }
export function Henter({ tekst = 'Henter …' }: { tekst?: string }) { return <div className="henter" aria-live="polite">{tekst}</div>; }

export const STATUS: Record<BookingStatus, [string, string]> = {
  afventer: ['Afventer godkendelse', 'b-gul'], godkendt: ['Godkendt', 'b-groen'], i_gang: ['I gang', 'b-lime'], faerdig: ['Færdig', 'b-graa'],
  aflyst: ['Aflyst', 'b-roed'], skal_rykkes: ['Skal rykkes', 'b-roed'], afventer_dato: ['Mangler dato', 'b-blaa'], mangler_tid: ['Mangler tid', 'b-blaa'],
};
export const statusTekst = (s: BookingStatus) => STATUS[s][0];
export function StatusMaerke({ status }: { status: BookingStatus }) { const t = useT(); const [x, c] = STATUS[status]; return <span className={'badge ' + c}>{t(x === 'Afventer godkendelse' ? 'Afventer godkendelse ' : x)}</span>; }

// ---------- Sprog (kun medarbejdere; kunder ser dansk) ----------
export function useSprog(): Sprog {
  const api = useApi();
  const hent = () => { const b = api.bruger(); return b && b.rolle !== 'kunde' ? b.sprog ?? 'da' : 'da'; };
  return useSyncExternalStore((f) => api.lyt(f), hent);
}
export function useT(): (tekst: string) => string {
  const s = useSprog();
  return useCallback((tekst: string) => oversaet(tekst, s), [s]);
}
/** Sætter sprog og skriveretning (arabisk og farsi læses fra højre) på hele siden. */
tilfoejOrd(ORD);
export function useSprogPaaSiden() {
  const s = useSprog();
  useEffect(() => { const r = document.documentElement; r.lang = s; r.dir = erRtl(s) ? 'rtl' : 'ltr'; void saetSidesprog(s); }, [s]);
}
export function SprogValg({ kompakt }: { kompakt?: boolean }) {
  const api = useApi();
  const s = useSprog();
  const t = useT();
  return (
    <select className={kompakt ? 'sprog-kompakt' : 'input'} aria-label={t('Sprog')} value={s} onChange={(e) => { void api.saetSprog(e.target.value as Sprog); }}>
      {SPROG.map((x) => <option key={x.id} value={x.id}>{x.navn}</option>)}
    </select>
  );
}
export function TemaKnap() {
  const tema = useTema();
  const t = useT();
  const moerk = tema === 'moerk' || (tema === 'auto' && erMoerk());
  return <button className="ikonknap tema-knap" aria-label={moerk ? t('Lyst tema') : t('Mørkt tema')} title={moerk ? t('Lyst tema') : t('Mørkt tema')} onClick={() => saetTema(moerk ? 'lys' : 'moerk')}><Ikon navn={moerk ? 'sol' : 'maane'} /></button>;
}

/** Escape lukker kun det øverste vindue (fx et billede åbnet fra en skuffe – ikke skuffen bagved). */
const escStak: object[] = [];
export function useEscape(luk: () => void) {
  const ref = useRef(luk); ref.current = luk;
  useEffect(() => {
    const mig = {};
    escStak.push(mig);
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape' && escStak[escStak.length - 1] === mig) { e.stopPropagation(); ref.current(); } };
    document.addEventListener('keydown', f);
    const gl = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', f); document.body.style.overflow = gl; escStak.splice(escStak.indexOf(mig), 1); };
  }, []);
}
export function Modal({ titel, luk, children, bred }: { titel: string; luk: () => void; children: ReactNode; bred?: boolean }) {
  useEscape(luk);
  return (
    <div className="slør midt" onMouseDown={(e) => { if (e.target === e.currentTarget) luk(); }}>
      <div className={'modal' + (bred ? ' bred' : '')} role="dialog" aria-modal="true" aria-label={titel}>
        <div className="raekke mellem ingen-ombryd"><h2>{titel}</h2><button className="ikonknap" onClick={luk} aria-label="Luk"><Ikon navn="x" /></button></div>
        {children}
      </div>
    </div>
  );
}
/** Skuffe fra højre. `tilbage` viser en pil, der går et skridt tilbage (fx fra kunden til bookingen); krydset lukker det hele. */
export function Skuffe({ titel, luk, children, fod, bred, under, tilbage }: { titel: ReactNode; luk: () => void; children: ReactNode; fod?: ReactNode; bred?: boolean; under?: ReactNode; tilbage?: () => void }) {
  useEscape(tilbage ?? luk);
  return (
    <div className="slør" onMouseDown={(e) => { if (e.target === e.currentTarget) luk(); }}>
      <div className={'skuffe' + (bred ? ' bred' : '')} role="dialog" aria-modal="true">
        <div className="skuffe-hoved">{tilbage && <button className="ikonknap" onClick={tilbage} aria-label="Tilbage"><Ikon navn="venstre" /></button>}<div className="fyld"><h2>{titel}</h2>{under && <div className="lille-tekst">{under}</div>}</div><button className="ikonknap" onClick={luk} aria-label="Luk"><Ikon navn="x" /></button></div>
        <div className="skuffe-krop">{children}</div>
        {fod && <div className="skuffe-fod">{fod}</div>}
      </div>
    </div>
  );
}

/** Bekræftelse inde på siden (browserens confirm() virker ikke i visningen). */
export function Bekraeft({ titel, tekst, knap, fare, luk, ok, children, kraevTekst }: {
  titel: string; tekst?: ReactNode; knap: string; fare?: boolean; luk: () => void; ok: (svar: string) => Promise<unknown> | void; children?: ReactNode; kraevTekst?: string;
}) {
  const [svar, setSvar] = useState('');
  const h = useHandling();
  return (
    <Modal titel={titel} luk={luk}>
      {tekst && <div className="svag">{tekst}</div>}
      {children}
      {kraevTekst !== undefined && (
        <Felt etiket={kraevTekst}><input value={svar} onChange={(e) => setSvar(e.target.value)} autoFocus /></Felt>
      )}
      <FejlBesked fejl={h.fejl} />
      <div className="raekke" style={{ justifyContent: 'flex-end' }}>
        <button className="knap stille" onClick={luk}>Fortryd</button>
        <button className={'knap ' + (fare ? 'fare' : 'primaer')} disabled={h.arbejder} onClick={async () => { const r = await h.koer(async () => { await ok(svar); return true; }); if (r) luk(); }}>{knap}</button>
      </div>
    </Modal>
  );
}

/** Kopiér-knap med fallback (markér teksten), da udklipsholderen ikke altid er tilladt. */
export function Kopier({ tekst, etiket = 'Kopiér', aria }: { tekst: string; etiket?: string; aria?: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="knap sekundaer lille" aria-label={aria} onClick={() => {
      navigator.clipboard?.writeText(tekst).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); }).catch(() => visToast('Markér teksten og kopiér den manuelt.'));
    }}><Ikon navn={ok ? 'check' : 'kopier'} lille />{ok ? 'Kopieret' : etiket}</button>
  );
}

// ---------- Hjælpere ----------
export const initialer = (navn: string) => navn.split(/\s+/).filter(Boolean).slice(0, 2).map((d) => d[0]!.toUpperCase()).join('');
export function tilMin(t: string): number | null { const m = formaterTid(t).match(/^(\d{1,2})[:.](\d{2})$/); if (!m) return null; const v = Number(m[1]) * 60 + Number(m[2]); return v >= 0 && v <= 1440 && Number(m[2]) < 60 ? v : null; }
/** Fælles tidsfelt: "930" og "0930" bliver til "09:30", "9" til "09:00" og "9.30" til "09:30" – mens man taster (kolon sættes selv). */
export function formaterTid(t: string): string {
  const r = t.trim().replace(/\s/g, '');
  if (/^\d{1,2}[:.]\d{1,2}$/.test(r)) { const [h, m] = r.split(/[:.]/); return h.padStart(2, '0') + ':' + m.padStart(2, '0'); }
  if (/^\d{3,4}$/.test(r)) { const h = r.slice(0, r.length - 2), m = r.slice(-2); return h.padStart(2, '0') + ':' + m; }
  if (/^\d{1,2}$/.test(r)) return r.padStart(2, '0') + ':00';
  return r;
}
export const tid = klokken;   // "HH:MM" (core/dato.ts)
/** Dansk telefonnummer til visning: "+4520304050" → "20 30 40 50". */
export const tlfTekst = (t: string) => t.replace('+45', '').replace(/(\d{2})(?=\d)/g, '$1 ').trim();
export const tidspunkt = (iso: string) => new Intl.DateTimeFormat('da-DK', { timeZone: 'Europe/Copenhagen', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
export const kortDato = (dato: string) => { const [y, m, d] = dato.split('-').map(Number); return new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d))); };
export const kortDag = (dato: string) => { const [y, m, d] = dato.split('-').map(Number); return new Intl.DateTimeFormat('da-DK', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d))); };

/** Formindsker et billede til max 1600 px og JPEG, så det fylder mindre. */
export function formindskBillede(fil: File, max = 1600): Promise<string> {
  return new Promise((ok, fejl) => {
    if (!fil.type.startsWith('image/')) { fejl(new Error('Vælg et billede.')); return; }
    const r = new FileReader();
    r.onerror = () => fejl(new Error('Billedet kunne ikke læses.'));
    r.onload = () => {
      const img = new Image();
      img.onerror = () => fejl(new Error('Billedet kunne ikke læses.'));
      img.onload = () => {
        const s = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement('canvas'); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
        c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
        ok(c.toDataURL('image/jpeg', 0.82));
      };
      img.src = String(r.result);
    };
    r.readAsDataURL(fil);
  });
}
export function laesFil(fil: File): Promise<string> {
  return new Promise((ok, fejl) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => fejl(new Error('Filen kunne ikke læses.')); r.readAsDataURL(fil); });
}

/** Google Kalender-link (i stedet for en .ics-fil, som ikke kan hentes i visningen). */
export function kalenderLink(titel: string, dato: string, start: number, slut: number, sted: string, tekst: string): string {
  const f = (m: number) => dato.replace(/-/g, '') + 'T' + String(Math.floor(m / 60)).padStart(2, '0') + String(m % 60).padStart(2, '0') + '00';
  const p = new URLSearchParams({ action: 'TEMPLATE', text: titel, dates: `${f(start)}/${f(slut)}`, ctz: 'Europe/Copenhagen', location: sted, details: tekst });
  return 'https://calendar.google.com/calendar/render?' + p.toString();
}

// ---------- Når en fil ikke kan gemmes (fx i testvisningen) ----------
let eksportTekst: { titel: string; tekst: string | null } | null = null;
const eksportLyttere = new Set<() => void>();
/** Vis CSV-teksten til kopiering, eller en forklaring for PDF, når filen ikke kunne gemmes. */
export function visEksport(titel: string, tekst: string | null) { eksportTekst = { titel, tekst }; eksportLyttere.forEach((f) => f()); }
export function EksportVaert() {
  const e = useSyncExternalStore((f) => { eksportLyttere.add(f); return () => eksportLyttere.delete(f); }, () => eksportTekst);
  if (!e) return null;
  const luk = () => { eksportTekst = null; eksportLyttere.forEach((f) => f()); };
  return (
    <Modal titel={e.titel} luk={luk} bred>
      {e.tekst != null ? <>
        <p className="svag">Testvisningen kan ikke gemme filer direkte. Kopiér indholdet og indsæt det i Excel eller Numbers – i driftversionen hentes filen med det samme.</p>
        <textarea className="input" readOnly value={e.tekst.replace(/^\uFEFF/, '')} style={{ minHeight: 220, fontFamily: 'ui-monospace, monospace', fontSize: 12 }} onFocus={(x) => x.currentTarget.select()} />
        <div className="raekke"><Kopier tekst={e.tekst.replace(/^\uFEFF/, '').replace(/;/g, '\t')} etiket="Kopiér til Excel" /></div>
      </> : <p className="svag">Testvisningen kan ikke gemme filer direkte. I driftversionen hentes PDF’en med det samme. Brug CSV her i testen for at se det filtrerede indhold.</p>}
      <button className="knap primaer" onClick={luk}>Luk</button>
    </Modal>
  );
}

// ---------- Freemi-logo: app-ikonet med den samme Freemi som agenten (brandmanualen) ----------
export function PixieLogo({ str = 34 }: { str?: number }) {
  return <img className="pixie-logo" src={pixieIkon} width={str} height={str} alt="" aria-hidden="true" draggable={false} style={{ width: str, height: str }} />;
}
// ---------- Bookingen indbygget på klientens hjemmeside (widget.js: på siden eller i et vindue) ----------
let indlejret: 'side' | 'vindue' | null = null;
export function saetIndlejret(v: 'side' | 'vindue' | null) { indlejret = v; }
export const erIndlejret = () => !!indlejret;
/** Luk vinduet på klientens hjemmeside (widget.js lytter efter beskeden). */
export function lukIndlejret() { try { window.parent.postMessage({ pixie: 'luk' }, '*'); } catch { /* ok */ } }
/** Bookingen står på hjemmesiden: bed siden rulle op til bookingen (nyt trin), hvis den er rullet ud af syne. */
export function rulIndlejret() { if (indlejret === 'side') try { window.parent.postMessage({ freemi: 'rul' }, '*'); } catch { /* ok */ } }
/** Kryds i bookingen: kun når den er åbnet i et vindue på hjemmesiden – ikke når den står direkte på siden. */
export const indlejretLuk = () => (indlejret === 'vindue' ? lukIndlejret : undefined);

/** Klienten (fx SteamGreen): navn, brand og moduler. Opdateres live, når brandet ændres. */
export function useKlient() { const api = useApi(); return useData(() => api.klient()).data; }
/** Klientens logo og navn – bruges i menuer og topbjælker i stedet for Freemis logo. */
export function KlientMaerke({ under, kompakt }: { under?: ReactNode; kompakt?: boolean }) {
  const kl = useKlient();
  const br = useBrandVisning();   // bookingsiden under et subdomæne: brandets navn og logo
  const k = kl && br ? { ...kl, navn: br.navn, brand: { ...kl.brand, logo: br.logo } } : kl;
  if (!k) return <div className="klient-maerke" />;
  return (
    <div className="klient-maerke">
      {k.brand.logo ? <img className="klient-logo" src={k.brand.logo} alt={k.navn} /> : <span className="klient-initial" aria-hidden="true">{k.navn.slice(0, 1).toUpperCase()}</span>}
      {(!k.brand.logo || !kompakt) && <div style={{ minWidth: 0 }}>{!k.brand.logo && <div className="klient-navn" data-ikke-oversaet>{k.navn}</div>}{under && <div className="klient-under">{under}</div>}</div>}
    </div>
  );
}
/** Lille "Drevet af Freemi" – Freemi er platformen bag klientens system. */
export function DrevetAfPixie({ version }: { version?: string }) {
  return <span className="drevet-af"><PixieLogo str={16} />Drevet af Freemi{version ? ` · ${version}` : ''}</span>;
}
export { PixieAgent } from './pixieAgent';

/** Smal skærm (mobil). Opdateres, når skærmen drejes eller vinduet ændrer størrelse. */
const MOBIL = '(max-width: 760px)';
export function useErMobil(): boolean {
  return useSyncExternalStore((f) => { try { const m = matchMedia(MOBIL); m.addEventListener('change', f); return () => m.removeEventListener('change', f); } catch { return () => {}; } },
    () => { try { return matchMedia(MOBIL).matches; } catch { return false; } }, () => false);
}

/** Viser om skærmen er live (ændringer fra andre ses med det samme). */
export function useLiveStatus(): LiveStatus { return useSyncExternalStore((f) => live.lytStatus(f), () => live.status); }
export function LiveMaerke({ kompakt }: { kompakt?: boolean }) {
  const s = useLiveStatus();
  const t = useT();
  const tekst = s === 'live' ? t('Live') : s === 'forbinder' ? t('Forbinder …') : t('Offline – opdaterer, når du er online igen');
  return <span className={'live-maerke ' + s} title={s === 'live' ? t('Ændringer fra kolleger og kunder vises med det samme') : tekst} role="status" aria-label={tekst}><i />{!kompakt && <span>{tekst}</span>}</span>;
}
